import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { PendingDecision, Pillar } from '@/lib/homeSummaries';

export interface ApprovedSummary { id: string; title: string; summary: string | null; reviewed_at: string | null; created_at: string; }

// Executive Brief data: only decisions that need the executive, strategy, and AI output a person has already
// approved. Deliberately no tasks, inbox or unreviewed AI text. Tenant data, RLS-scoped; queries are independent
// so one failing table leaves its panel saying so instead of blanking the screen.
export function useExecutiveData() {
  const decisions = useQuery({
    queryKey: ['home', 'exec', 'decisions'],
    staleTime: 60_000,
    queryFn: async (): Promise<PendingDecision[]> => {
      const { data, error } = await supabase.from('decisions')
        .select('id,title,project_name,due_date,priority,amount').eq('status', 'pending').limit(50);
      if (error) throw new Error(error.message);
      // decisions.amount is free text ("250000", "SAR 1.2M"…). Only a plain number counts as a recorded amount;
      // anything else is treated as not recorded rather than guessed at.
      return (data ?? []).map((d) => {
        const n = d.amount && /^\s*-?\d+(\.\d+)?\s*$/.test(d.amount) ? Number(d.amount) : null;
        return { ...d, amount: n };
      });
    },
  });
  const pillars = useQuery({
    queryKey: ['home', 'exec', 'pillars'],
    staleTime: 60_000,
    queryFn: async (): Promise<Pillar[]> => {
      const { data, error } = await supabase.from('strategy_pillars')
        .select('id,name,status,progress_pct,target_pct').order('display_order');
      if (error) throw new Error(error.message);
      return (data ?? []) as Pillar[];
    },
  });
  const approved = useQuery({
    queryKey: ['home', 'exec', 'approved'],
    staleTime: 60_000,
    queryFn: async (): Promise<ApprovedSummary[]> => {
      const { data, error } = await supabase.from('ai_outputs')
        .select('id,title,summary,reviewed_at,created_at').eq('status', 'approved')
        .order('reviewed_at', { ascending: false, nullsFirst: false }).limit(5);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as ApprovedSummary[];
    },
  });
  return {
    decisions: decisions.data ?? [], decisionsFailed: decisions.isError,
    /** null = could not be read (not the same as no pillars defined). */
    pillars: pillars.isError ? null : pillars.data ?? [],
    approved: approved.data ?? [], approvedFailed: approved.isError,
    isLoading: decisions.isLoading || pillars.isLoading || approved.isLoading,
  };
}
